/* =========================================================================
   LumiCat landing — purchase + gallery behaviour for lc-* sections.

   Custom elements (each defined once, listeners cleaned up on disconnect,
   so Theme Editor section reloads never stack handlers):
     <lc-product>  hero: gallery, lightbox, option pickers, offers, quantity, add to cart
     <lc-cta>      final call-to-action that mirrors and drives <lc-product>
     <lc-sticky>   sticky add-to-cart bar

   Sections talk through two namespaced document events, scoped by productId:
     lc:state    { productId, ... }  — emitted by <lc-product> after every change
     lc:command  { productId, action, ... } — sent to <lc-product>
       actions: request-state | select-offer {key} | set-option {unit, optionIndex|optionName, value, scroll}
                | add | focus

   Cart writes go through Shopify.actions.updateCart (Horizon's native cart
   pipeline: cart drawer + header count refresh). Without it, the script
   falls back to /cart/add.js and then the cart page.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__lcProductLoaded) return;
  window.__lcProductLoaded = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------------------------------------------------------------------
     Helpers
  --------------------------------------------------------------------- */
  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  function formatMoney(cents, format) {
    var value = Number(cents) || 0;
    var fmt = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var match = fmt.match(placeholder);
    var out;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        out = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        out = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        out = withDelimiters(value, 2, ',', '.');
    }
    return fmt.replace(placeholder, out);
  }

  function fill(template, map) {
    var out = String(template || '');
    Object.keys(map).forEach(function (key) {
      out = out.split('[' + key + ']').join(map[key]);
    });
    return out;
  }

  function defineOnce(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /**
   * Adds lines to the cart through the theme's native pipeline.
   * @param {Array<{id:number, quantity:number, properties?:Object}>} items
   * @returns {Promise<void>}
   */
  function addLines(items) {
    var actions = window.Shopify && window.Shopify.actions;
    if (actions && typeof actions.updateCart === 'function') {
      var lines = items.map(function (item) {
        var line = { merchandiseId: String(item.id), quantity: item.quantity };
        if (item.properties) {
          line.attributes = Object.keys(item.properties).map(function (key) {
            return { key: key, value: String(item.properties[key]) };
          });
        }
        return line;
      });
      return actions.updateCart({ lines: lines }, { event: { context: 'product' } }).then(function (result) {
        var errors = (result && result.userErrors) || [];
        if (errors.length) throw new Error(errors[0].message);
      });
    }

    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok || json.status) throw new Error(json.description || json.message || 'Error');
          return json;
        });
      })
      .then(function () {
        window.location.href = ROOT + 'cart';
      });
  }

  function openCart() {
    var actions = window.Shopify && window.Shopify.actions;
    if (actions && typeof actions.openCart === 'function') {
      return actions.openCart();
    }
    window.location.href = ROOT + 'cart';
    return Promise.resolve();
  }

  /* ---------------------------------------------------------------------
     <lc-product>
  --------------------------------------------------------------------- */
  class LcProduct extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();
      var signal = this._abort.signal;

      var json = this.querySelector('[data-lc-product-json]');
      try {
        this.data = JSON.parse(json ? json.textContent : '{}');
      } catch (e) {
        this.data = null;
      }
      if (!this.data || !this.data.variants) return;

      this.productId = Number(this.data.productId);
      this.labels = this.data.labels || {};
      this.form = this.querySelector('[data-lc-form]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-lc-slide]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-lc-thumb]'));
      this.lightbox = this.querySelector('[data-lc-lightbox]');
      this.lightboxImg = this.querySelector('[data-lc-lightbox-img]');
      this.qtyInput = this.querySelector('[data-lc-qty-input]');
      this.submitButton = this.querySelector('[data-lc-submit]');
      this.mediaIndex = Math.max(0, Number(this.data.startMedia) || 0);
      this.submitting = false;

      this.offerKey = this.data.selectedOffer || (this.data.offers[0] && this.data.offers[0].key) || null;

      /* Option inputs */
      this.addEventListener(
        'change',
        (event) => {
          var target = event.target;
          if (!(target instanceof HTMLInputElement)) return;
          if (target.hasAttribute('data-lc-offer')) {
            this.offerKey = target.value;
            this.update();
            return;
          }
          if (target.hasAttribute('data-lc-value')) {
            var unitEl = target.closest('[data-lc-unit]');
            var optionEl = target.closest('[data-lc-option]');
            this.update();
            if (unitEl && unitEl.getAttribute('data-lc-unit') === '1' && optionEl) {
              this.showVariantMedia(this.unitVariant(1));
            }
            return;
          }
          if (target === this.qtyInput) this.update();
        },
        { signal: signal }
      );

      /* Quantity buttons */
      this.querySelectorAll('[data-lc-qty-step]').forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            var step = Number(button.getAttribute('data-lc-qty-step'));
            this.setQuantity(this.quantity() + step);
          },
          { signal: signal }
        );
      });
      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => this.update(), { signal: signal });
        this.qtyInput.addEventListener('blur', () => this.setQuantity(this.quantity()), { signal: signal });
      }

      /* Gallery */
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener(
          'click',
          () => this.showMedia(Number(thumb.getAttribute('data-lc-thumb'))),
          { signal: signal }
        );
      });
      this.querySelectorAll('[data-lc-step]').forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            this.showMedia(this.mediaIndex + Number(button.getAttribute('data-lc-step')));
          },
          { signal: signal }
        );
      });
      this.bindSwipe(signal);

      var zoomOpen = this.querySelector('[data-lc-zoom-open]');
      if (zoomOpen && this.lightbox) {
        zoomOpen.addEventListener('click', () => this.openLightbox(), { signal: signal });
        this.querySelector('[data-lc-zoom-close]').addEventListener(
          'click',
          () => this.lightbox.close(),
          { signal: signal }
        );
        this.lightbox.addEventListener(
          'click',
          (event) => {
            if (event.target === this.lightbox) this.lightbox.close();
          },
          { signal: signal }
        );
        this.lightbox.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'ArrowRight') this.showMedia(this.mediaIndex + 1);
            if (event.key === 'ArrowLeft') this.showMedia(this.mediaIndex - 1);
          },
          { signal: signal }
        );
      }

      /* Submit */
      if (this.form) {
        this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal: signal });
      }

      /* Commands from other sections */
      document.addEventListener(
        'lc:command',
        (event) => {
          var d = event.detail || {};
          if (Number(d.productId) !== this.productId) return;
          this.onCommand(d);
        },
        { signal: signal }
      );

      /* Theme Editor: keep the selected offer block visible when chosen in the sidebar */
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          var input = event.target && event.target.querySelector && event.target.querySelector('[data-lc-offer]');
          if (input) {
            input.checked = true;
            this.offerKey = input.value;
            this.update();
          }
        },
        { signal: signal }
      );

      this.update();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
    }

    /* ----- data access ----- */
    offer() {
      var key = this.offerKey;
      var offers = this.data.offers || [];
      return offers.find((o) => o.key === key) || offers[0] || { key: null, units: 1, bundle: null, title: '', ctaTitle: '' };
    }

    unitEl(n) {
      return this.querySelector('[data-lc-unit="' + n + '"]');
    }

    unitOptions(n) {
      var unit = this.unitEl(n);
      if (!unit) return null;
      var values = [];
      this.data.options.forEach((option, index) => {
        var checked = unit.querySelector('[data-lc-option="' + index + '"] [data-lc-value]:checked');
        var fixed = unit.querySelector('[data-lc-fixed-value][data-option-index="' + index + '"]');
        values[index] = checked ? checked.value : fixed ? fixed.value : null;
      });
      return values;
    }

    findVariant(options) {
      if (!options) return null;
      return (
        this.data.variants.find((variant) =>
          variant.options.every((value, index) => value === options[index])
        ) || null
      );
    }

    unitVariant(n) {
      return this.findVariant(this.unitOptions(n));
    }

    quantity() {
      if (!this.qtyInput) return 1;
      var q = parseInt(this.qtyInput.value, 10);
      return isNaN(q) ? 1 : q;
    }

    quantityBounds() {
      var min = Number(this.data.quantity.min) || 1;
      var max = Number(this.data.quantity.max) || 99;
      var v = this.unitVariant(1);
      if (v && v.quantityRule && v.quantityRule.max) max = Math.min(max, v.quantityRule.max);
      return { min: min, max: Math.max(min, max) };
    }

    setQuantity(q) {
      if (!this.qtyInput) return;
      var bounds = this.quantityBounds();
      this.qtyInput.value = String(Math.max(bounds.min, Math.min(bounds.max, q || bounds.min)));
      this.update();
    }

    /** Price maths for one offer using the current unit selections. */
    priceFor(offer) {
      var variants = [];
      var units = offer.units || 1;
      for (var n = 1; n <= units; n++) variants.push(this.unitVariant(n));
      var complete = variants.every(Boolean);
      var singles = 0;
      var compareSingles = 0;
      variants.forEach(function (v) {
        if (!v) return;
        singles += v.price;
        compareSingles += Math.max(v.compareAtPrice || 0, v.price);
      });
      var price = singles;
      var pct = Number(offer.discount) || 0;
      if (pct > 0 && !offer.bundle) {
        /* Display mirror of the store's automatic discount (set in Admin → Discounts). */
        price = variants.reduce(function (sum, v) {
          return v ? sum + Math.round((v.price * (100 - pct)) / 100) : sum;
        }, 0);
      }
      var save = compareSingles - price;
      var available = complete && variants.every(function (v) { return v.available; });
      if (offer.bundle) {
        price = offer.bundle.price;
        save = compareSingles - price;
        available = available && offer.bundle.available;
      }
      return { variants: variants, complete: complete, price: price, save: Math.max(0, save), available: available };
    }

    /* ----- rendering ----- */
    update() {
      var offer = this.offer();
      var units = offer.units || 1;
      var labels = this.labels;
      var fmt = this.data.moneyFormat;
      var swatchIndex = Number(this.data.swatchOptionIndex);

      /* unit visibility + legends */
      this.querySelectorAll('[data-lc-unit]').forEach((unit) => {
        var n = Number(unit.getAttribute('data-lc-unit'));
        var active = n <= units;
        unit.hidden = !active;
        unit.disabled = !active;
        var currentOptions = this.unitOptions(n);
        unit.querySelectorAll('[data-lc-option]').forEach((group) => {
          var index = Number(group.getAttribute('data-lc-option'));
          var labelEl = group.querySelector('[data-lc-option-label]');
          var valueEl = group.querySelector('[data-lc-option-value]');
          var optionName = this.data.options[index].name;
          var label = optionName;
          if (index === swatchIndex && labels.swatchOption) label = labels.swatchOption;
          if (index === swatchIndex && units > 1) label = fill(labels.unit, { n: n });
          if (labelEl) labelEl.textContent = label;
          if (valueEl) valueEl.textContent = currentOptions[index] || '';

          /* disable values that don't exist as a variant with the other choices */
          group.querySelectorAll('[data-lc-value]').forEach((input) => {
            var trial = currentOptions.slice();
            trial[index] = input.value;
            var match = this.findVariant(trial);
            input.disabled = !match;
            var host = input.closest('.lc-swatch, .lc-pill');
            if (host) host.classList.toggle('is-soldout', Boolean(match && !match.available));
          });
        });
        var v = this.findVariant(currentOptions);
        unit.setAttribute('data-variant-id', v ? String(v.id) : '');
      });

      /* unit-1 price line */
      var first = this.unitVariant(1);
      var unitPrice = this.querySelector('[data-lc-unit-price]');
      var unitCompare = this.querySelector('[data-lc-unit-compare]');
      if (first && unitPrice) unitPrice.textContent = formatMoney(first.price, fmt);
      if (unitCompare) {
        var hasCompare = Boolean(first && first.compareAtPrice > first.price);
        unitCompare.hidden = !hasCompare;
        unitCompare.textContent = hasCompare ? formatMoney(first.compareAtPrice, fmt) : '';
      }

      /* offers */
      var offerStates = (this.data.offers || []).map((o) => {
        var calc = this.priceFor(o);
        var priceText = formatMoney(calc.price, fmt);
        var saveText = calc.save > 0 ? fill(labels.save, { amount: formatMoney(calc.save, fmt) }) : '';
        var priceEl = this.querySelector('[data-lc-offer-price="' + o.key + '"]');
        var saveEl = this.querySelector('[data-lc-offer-save="' + o.key + '"]');
        var eachEl = this.querySelector('[data-lc-offer-each="' + o.key + '"]');
        var hintEl = this.querySelector('[data-lc-multi-hint="' + o.key + '"]');
        var wasEl = this.querySelector('[data-lc-offer-was="' + o.key + '"]');
        if (priceEl) priceEl.textContent = priceText;
        if (wasEl) {
          var was = calc.price + calc.save;
          wasEl.hidden = !(calc.save > 0);
          wasEl.textContent = calc.save > 0 ? formatMoney(was, fmt) : '';
        }
        if (saveEl) {
          saveEl.hidden = !saveText;
          saveEl.textContent = saveText;
        }
        if (eachEl && o.units > 1) {
          eachEl.textContent = ' · ' + fill(labels.each, { price: formatMoney(Math.round(calc.price / o.units), fmt) });
        }
        if (hintEl && labels.multiNote) {
          hintEl.textContent = fill(labels.multiNote, { count: o.units, price: priceText });
        }
        return {
          key: o.key,
          title: o.ctaTitle || o.title,
          units: o.units,
          priceFormatted: priceText,
          saveFormatted: saveText,
          selected: o.key === offer.key,
        };
      });

      /* quantity */
      var bounds = this.quantityBounds();
      var qty = this.quantity();
      if (this.qtyInput) {
        this.qtyInput.max = String(bounds.max);
        this.qtyInput.min = String(bounds.min);
        this.querySelectorAll('[data-lc-qty-step]').forEach((button) => {
          var step = Number(button.getAttribute('data-lc-qty-step'));
          button.disabled = step < 0 ? qty <= bounds.min : qty >= bounds.max;
        });
        var unitLabel = this.querySelector('[data-lc-qty-unit]');
        if (unitLabel) {
          unitLabel.textContent =
            units > 1 ? (qty > 1 ? labels.packOther : labels.packOne) : qty > 1 ? labels.qtyOther : labels.qtyOne;
        }
      }
      var maxEl = this.querySelector('[data-lc-max]');
      if (maxEl) {
        var atMax = this.qtyInput && qty >= bounds.max && labels.maxReached;
        maxEl.hidden = !atMax;
        maxEl.textContent = atMax ? fill(labels.maxReached, { max: bounds.max }) : '';
      }

      /* totals + CTA */
      var calc = this.priceFor(offer);
      var total = calc.price * qty;
      var saving = calc.save * qty;
      this.state = { calc: calc, qty: qty, total: total, offer: offer };

      var savingsEl = this.querySelector('[data-lc-savings]');
      if (savingsEl) {
        var showSave = saving > 0 && labels.savings;
        savingsEl.hidden = !showSave;
        savingsEl.textContent = showSave ? fill(labels.savings, { amount: formatMoney(saving, fmt) }) : '';
      }

      var ctaText = this.ctaText();
      var ctaLabel = this.querySelector('[data-lc-cta-label]');
      if (ctaLabel && !this.submitting) ctaLabel.textContent = ctaText;
      if (this.submitButton) this.submitButton.disabled = this.submitting || !calc.available;

      /* no-JS fallback field mirrors unit 1 */
      var fallback = this.querySelector('[data-lc-fallback-id]');
      if (fallback && first) fallback.value = String(first.id);

      emit('lc:state', {
        productId: this.productId,
        sectionId: this.data.sectionId,
        offers: offerStates,
        offerKey: offer.key,
        available: calc.available,
        complete: calc.complete,
        totalFormatted: formatMoney(total, fmt),
        ctaText: ctaText,
        summary: this.summary(),
        image: first && first.image ? first.image : null,
        submitting: this.submitting,
      });
    }

    ctaText() {
      var calc = this.state && this.state.calc;
      var labels = this.labels;
      if (!calc || !calc.complete) return labels.unavailable;
      if (!calc.available) return labels.soldOut;
      if (labels.ctaShowTotal) return labels.cta + ' — ' + formatMoney(this.state.total, this.data.moneyFormat);
      return labels.cta;
    }

    summary() {
      var offer = this.offer();
      var units = offer.units || 1;
      var swatchIndex = Number(this.data.swatchOptionIndex);
      var parts = [];
      for (var n = 1; n <= units; n++) {
        var options = this.unitOptions(n) || [];
        var visible = [];
        options.forEach((value, index) => {
          if (this.data.options[index] && this.data.options[index].values.length > 1) visible.push(value);
        });
        if (swatchIndex >= 0 && options[swatchIndex]) visible = [options[swatchIndex]].concat(
          visible.filter(function (v) { return v !== options[swatchIndex]; })
        );
        parts.push(visible.join(' / '));
      }
      var head = offer.ctaTitle || offer.title || this.data.productTitle;
      var line = head + (parts.join('') ? ' · ' + parts.join(' + ') : '');
      var qty = this.quantity();
      if (qty > 1) line += ' · ×' + qty;
      return line;
    }

    /* ----- gallery ----- */
    showMedia(index) {
      if (!this.slides.length) return;
      var count = this.slides.length;
      this.mediaIndex = ((index % count) + count) % count;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.mediaIndex;
        if (i === this.mediaIndex) {
          var img = slide.querySelector('img');
          if (img && img.loading === 'lazy') img.loading = 'eager';
        }
      });
      this.thumbs.forEach((thumb, i) => {
        thumb.setAttribute('aria-current', i === this.mediaIndex ? 'true' : 'false');
        if (i === this.mediaIndex && thumb.scrollIntoView) {
          var strip = thumb.parentElement;
          if (strip && strip.scrollWidth > strip.clientWidth) {
            strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
          }
        }
      });
      if (this.lightbox && this.lightbox.open) this.syncLightbox();
    }

    showVariantMedia(variant) {
      if (!variant || !variant.mediaPosition) return;
      var index = this.slides.findIndex(function (slide) {
        return Number(slide.getAttribute('data-media-position')) === variant.mediaPosition;
      });
      if (index >= 0) this.showMedia(index);
    }

    syncLightbox() {
      var slide = this.slides[this.mediaIndex];
      if (!slide || !this.lightboxImg) return;
      this.lightboxImg.src = slide.getAttribute('data-zoom-src');
      this.lightboxImg.alt = slide.getAttribute('data-alt') || '';
    }

    openLightbox() {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this.syncLightbox();
      this.lightbox.showModal();
    }

    bindSwipe(signal) {
      var stage = this.querySelector('.lc-gallery__stage');
      if (!stage || this.slides.length < 2) return;
      var startX = null;
      var startY = null;
      stage.addEventListener(
        'touchstart',
        function (event) {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true, signal: signal }
      );
      stage.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          var dx = event.changedTouches[0].clientX - startX;
          var dy = event.changedTouches[0].clientY - startY;
          startX = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            this.showMedia(this.mediaIndex + (dx < 0 ? 1 : -1));
          }
        },
        { passive: true, signal: signal }
      );
    }

    /* ----- commands ----- */
    onCommand(d) {
      switch (d.action) {
        case 'request-state':
          this.update();
          break;
        case 'select-offer': {
          var input = this.querySelector('[data-lc-offer="' + d.key + '"]');
          if (input) {
            input.checked = true;
            this.offerKey = d.key;
            this.update();
          }
          break;
        }
        case 'set-option': {
          var unit = this.unitEl(d.unit || 1);
          if (!unit) break;
          var index = typeof d.optionIndex === 'number' ? d.optionIndex : -1;
          if (index < 0 && d.optionName) {
            index = this.data.options.findIndex(function (o) {
              return o.name.toLowerCase() === String(d.optionName).toLowerCase();
            });
          }
          if (index < 0) index = Number(this.data.swatchOptionIndex);
          var radios = unit.querySelectorAll('[data-lc-option="' + index + '"] [data-lc-value]');
          radios.forEach(function (radio) {
            if (radio.value === d.value && !radio.disabled) radio.checked = true;
          });
          this.update();
          this.showVariantMedia(this.unitVariant(d.unit || 1));
          if (d.scroll) this.focusBuyBox();
          break;
        }
        case 'add':
          if (this.form) this.onSubmit(null);
          break;
        case 'focus':
          this.focusBuyBox();
          break;
      }
    }

    focusBuyBox() {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }

    /* ----- cart ----- */
    buildItems() {
      var offer = this.offer();
      var units = offer.units || 1;
      var qty = this.quantity();
      var calc = this.priceFor(offer);
      if (!calc.complete || !calc.available) return null;

      if (offer.bundle) {
        var properties = {};
        calc.variants.forEach((variant, i) => {
          properties[fill(this.labels.unit, { n: i + 1 })] = variant.title;
        });
        return [{ id: offer.bundle.variantId, quantity: qty, properties: properties }];
      }

      var merged = {};
      var order = [];
      for (var n = 0; n < units; n++) {
        var v = calc.variants[n];
        if (!merged[v.id]) {
          merged[v.id] = 0;
          order.push(v.id);
        }
        merged[v.id] += qty;
      }
      return order.map(function (id) {
        return { id: id, quantity: merged[id] };
      });
    }

    setStatus(type, message) {
      var error = this.querySelector('[data-lc-error]');
      var live = this.querySelector('[data-lc-live]');
      if (error) {
        error.hidden = type !== 'error';
        error.textContent = type === 'error' ? message : '';
      }
      if (live) live.textContent = type === 'error' ? '' : message || '';
    }

    onSubmit(event) {
      if (event) event.preventDefault();
      if (this.submitting) return;
      var items = this.buildItems();
      if (!items) return;

      this.submitting = true;
      var label = this.querySelector('[data-lc-cta-label]');
      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('is-loading');
        this.submitButton.setAttribute('aria-busy', 'true');
      }
      if (label) label.textContent = this.labels.adding;
      this.setStatus('info', this.labels.adding);
      this.update();

      addLines(items)
        .then(() => {
          this.setStatus('info', this.labels.added);
          emit('lc:added', { productId: this.productId, items: items });
          if (this.data.afterAdd === 'drawer') {
            return openCart();
          }
          if (label) label.textContent = this.labels.added;
          return new Promise(function (resolve) { setTimeout(resolve, 1600); });
        })
        .catch((error) => {
          this.setStatus('error', (error && error.message) || this.labels.error);
        })
        .then(() => {
          this.submitting = false;
          if (this.submitButton) {
            this.submitButton.classList.remove('is-loading');
            this.submitButton.removeAttribute('aria-busy');
          }
          this.update();
        });
    }
  }

  defineOnce('lc-product', LcProduct);

  /* ---------------------------------------------------------------------
     Shared fallback for mirrors (<lc-cta>, <lc-sticky>) when no <lc-product>
     answers on the page: submit their own native form through the cart pipeline.
  --------------------------------------------------------------------- */
  function fallbackSubmit(form, button) {
    var id = form.querySelector('[name="id"]');
    var qty = form.querySelector('[name="quantity"]');
    if (!id) return;
    if (button) button.disabled = true;
    addLines([{ id: Number(id.value), quantity: Number(qty ? qty.value : 1) || 1 }])
      .then(openCart)
      .catch(function () {
        form.submit();
      })
      .then(function () {
        if (button) button.disabled = false;
      });
  }

  /* ---------------------------------------------------------------------
     <lc-cta>
  --------------------------------------------------------------------- */
  class LcCta extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();
      var signal = this._abort.signal;
      this.productId = Number(this.getAttribute('data-product-id'));
      this.connected = false;
      this.offersEl = this.querySelector('[data-lc-cta-offers]');
      this.summaryEl = this.querySelector('[data-lc-cta-summary]');
      this.button = this.querySelector('[data-lc-cta-submit]');
      this.label = this.querySelector('[data-lc-cta-label]');
      this.form = this.querySelector('form');

      document.addEventListener('lc:state', (event) => this.render(event.detail || {}), { signal: signal });

      if (this.form) {
        this.form.addEventListener(
          'submit',
          (event) => {
            event.preventDefault();
            if (this.connected) {
              emit('lc:command', { productId: this.productId, action: 'add' });
            } else {
              fallbackSubmit(this.form, this.button);
            }
          },
          { signal: signal }
        );
      }

      if (this.offersEl) {
        this.offersEl.addEventListener(
          'click',
          (event) => {
            var button = event.target.closest('[data-lc-cta-offer]');
            if (!button) return;
            emit('lc:command', { productId: this.productId, action: 'select-offer', key: button.getAttribute('data-lc-cta-offer') });
          },
          { signal: signal }
        );
        this.offersEl.addEventListener(
          'keydown',
          (event) => {
            if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].indexOf(event.key) < 0) return;
            var buttons = Array.prototype.slice.call(this.offersEl.querySelectorAll('[data-lc-cta-offer]'));
            var index = buttons.indexOf(document.activeElement);
            if (index < 0) return;
            event.preventDefault();
            var dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
            var next = buttons[(index + dir + buttons.length) % buttons.length];
            this.pendingFocus = next.getAttribute('data-lc-cta-offer');
            emit('lc:command', { productId: this.productId, action: 'select-offer', key: this.pendingFocus });
          },
          { signal: signal }
        );
      }

      emit('lc:command', { productId: this.productId, action: 'request-state' });
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
    }

    render(state) {
      if (Number(state.productId) !== this.productId) return;
      this.connected = true;
      var showOffers = this.getAttribute('data-show-offers') === 'true';

      if (this.offersEl && showOffers && state.offers && state.offers.length > 1) {
        this.offersEl.hidden = false;
        var html = state.offers
          .map(function (o) {
            var title = o.title + (o.saveFormatted ? ' · ' + o.saveFormatted.toLowerCase() : '');
            return (
              '<button type="button" class="lc-cta__offer" role="radio" data-lc-cta-offer="' + o.key + '"' +
              ' aria-checked="' + (o.selected ? 'true' : 'false') + '" tabindex="' + (o.selected ? '0' : '-1') + '">' +
              '<span class="lc-cta__offer-title"></span><span class="lc-cta__offer-price"></span></button>'
            ).replace(
              '<span class="lc-cta__offer-title"></span>',
              '<span class="lc-cta__offer-title">' + escapeHtml(title) + '</span>'
            ).replace(
              '<span class="lc-cta__offer-price"></span>',
              '<span class="lc-cta__offer-price">' + escapeHtml(o.priceFormatted) + '</span>'
            );
          })
          .join('');
        this.offersEl.innerHTML = html;
        if (this.pendingFocus) {
          var focusTarget = this.offersEl.querySelector('[data-lc-cta-offer="' + this.pendingFocus + '"]');
          if (focusTarget) focusTarget.focus();
          this.pendingFocus = null;
        }
      }
      if (this.summaryEl) {
        this.summaryEl.hidden = false;
        this.summaryEl.textContent = state.summary || '';
      }
      if (this.label && state.ctaText) this.label.textContent = state.ctaText;
      if (this.button) this.button.disabled = Boolean(state.submitting) || !state.available;
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  defineOnce('lc-cta', LcCta);

  /* ---------------------------------------------------------------------
     <lc-sticky>
  --------------------------------------------------------------------- */
  class LcSticky extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();
      var signal = this._abort.signal;
      this.productId = Number(this.getAttribute('data-product-id'));
      this.bar = this.querySelector('.lc-sticky');
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-lc-sticky-submit]');
      this.connected = false;
      this.ctaHidden = false;
      this.cartOpen = false;

      document.addEventListener('lc:state', (event) => this.render(event.detail || {}), { signal: signal });

      if (this.form) {
        this.form.addEventListener(
          'submit',
          (event) => {
            event.preventDefault();
            if (this.connected) emit('lc:command', { productId: this.productId, action: 'add' });
            else fallbackSubmit(this.form, this.button);
          },
          { signal: signal }
        );
      }

      this.observeCta(signal);
      emit('lc:command', { productId: this.productId, action: 'request-state' });
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this.observer) this.observer.disconnect();
      if (this.blockerObserver) this.blockerObserver.disconnect();
    }

    observeCta(signal) {
      var cta = document.querySelector('lc-product[data-product-id="' + this.productId + '"] [data-lc-main-cta]');
      if (cta && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          var entry = entries[0];
          /* Show only once the main button has scrolled above the viewport. */
          this.ctaHidden = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggle();
        });
        this.observer.observe(cta);

        /* Step aside while the final CTA or the footer is on screen, so the bar never covers them. */
        /* lc-cta uses display: contents (no box), so observe its card wrapper instead. */
        var blockers = Array.prototype.slice
          .call(document.querySelectorAll('lc-cta[data-product-id="' + this.productId + '"], .shopify-section-group-footer-group, footer'))
          .map((el) => (el.tagName === 'LC-CTA' ? el.closest('.lc-cta') || el.parentElement : el))
          .filter(Boolean);
        if (blockers.length) {
          this.blocked = new Set();
          this.blockerObserver = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) this.blocked.add(e.target);
              else this.blocked.delete(e.target);
            });
            this.toggle();
          });
          blockers.forEach((el) => this.blockerObserver.observe(el));
        }
        return;
      }
      var onScroll = () => {
        this.ctaHidden = (window.scrollY || 0) > window.innerHeight * 0.85;
        this.toggle();
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal: signal });
      onScroll();
    }

    toggle() {
      if (!this.bar) return;
      var drawerOpen = Boolean(document.querySelector('theme-drawer#cart-drawer[open]'));
      var blocked = Boolean(this.blocked && this.blocked.size);
      var visible = this.ctaHidden && !drawerOpen && !blocked;
      this.bar.classList.toggle('is-visible', visible);
      this.bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.bar.inert = !visible;
    }

    render(state) {
      if (Number(state.productId) !== this.productId) return;
      this.connected = true;
      var total = this.querySelector('[data-lc-sticky-total]');
      var summary = this.querySelector('[data-lc-sticky-summary]');
      var thumb = this.querySelector('[data-lc-sticky-thumb]');
      if (total) total.textContent = state.totalFormatted || '';
      if (summary) summary.textContent = state.summary || '';
      if (thumb && state.image) thumb.src = state.image;
      if (this.button) this.button.disabled = Boolean(state.submitting) || !state.available;
      this.toggle();
    }
  }

  defineOnce('lc-sticky', LcSticky);

  /* ---------------------------------------------------------------------
     Colour tiles (benefits section): any [data-lc-pick] button selects an
     option value in the hero for the given product and scrolls to it.
  --------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var trigger = event.target instanceof Element ? event.target.closest('[data-lc-pick]') : null;
    if (!trigger) return;
    var productId = Number(trigger.getAttribute('data-product-id'));
    var hero = document.querySelector('lc-product[data-product-id="' + productId + '"]');
    if (!hero) return; /* let the link (if any) navigate normally */
    event.preventDefault();
    emit('lc:command', {
      productId: productId,
      action: 'set-option',
      unit: 1,
      optionName: trigger.getAttribute('data-option-name'),
      value: trigger.getAttribute('data-lc-pick'),
      scroll: true,
    });
  });
})();
